/* eslint-disable no-console */
import React, { FC, Suspense, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';

import {
  CROP_UNSUPPORTED_FORMATS,
  FILE_UPLOAD_STATUS,
  SUPPORTED_FILE_FORMATS
} from '@constants/common';
import {
  useGeneratePreSignedURLMutation,
  useUploadFileToS3Mutation
} from '@containers/api';
import {
  DeleteIcon,
  FileUploaderIcon,
  RefreshIcon,
  SuccessIcon,
  UploadFailedIcon
} from '@assets/icons';
import { translate } from '@utils/locale';
import { showGenericErrorToast } from '@utils/error';
import { convertStringToSnakeCase } from '@utils/common';
import { FileDropZone, ImageCropper, ImagePreviewCard } from '@components';

import { ImageUploaderPropsType } from '../types';

const ImageUploader: FC<ImageUploaderPropsType> = props => {
  const {
    name,
    enableCrop,
    sizeInMb,
    supportedFormats = SUPPORTED_FILE_FORMATS,
    fileUploadSuccessHandler,
    deleteFileHandler,
    errors,
    previousFile,
    aspectRatio,
    showPreview = false,
    isDisabled = false,
    imageInfo,
    fileUploadFailureHandler,
    resetUploadedFile,
    retryUpload,
    customUploadText,
    endpointUrl = 'deal-upload'
  } = props;

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [status, setStatus] = useState<string>('');

  const [generatePreSignedURL] = useGeneratePreSignedURLMutation();
  const [uploadFileToS3] = useUploadFileToS3Mutation();

  const handleFileUpload = (file: File) => {
    setUploadedFile(file);
    if (
      !enableCrop ||
      Object.values(CROP_UNSUPPORTED_FORMATS).includes(file.type)
    )
      handleUpload(file);
  };

  const bytesToMB = (bytes: number = 0) => {
    const megabytes = bytes / 1000000;
    return megabytes.toFixed(2);
  };

  const handleRemoveFileClick = () => {
    setUploadedFile(null);
    setStatus('');
    deleteFileHandler();
  };

  const handleUpload = async (file: File) => {
    setUploadedFile(file);
    setStatus(FILE_UPLOAD_STATUS.uploading);
    if (file.size > sizeInMb * 1000000) {
      setStatus(FILE_UPLOAD_STATUS.failed);
      return;
    }
    const payload = {
      url: endpointUrl,
      body: {
        content_type: file.type,
        content_name: convertStringToSnakeCase(file.name)
      }
    };
    try {
      const response = await generatePreSignedURL(payload).unwrap();
      if (response && response.result.upload_url) {
        const uploadFileToS3Request = uploadFileToS3({
          url: response.result.upload_url,
          payload: file,
          contentType: file.type
        });
        await uploadFileToS3Request.unwrap();
        setStatus(FILE_UPLOAD_STATUS.success);
        fileUploadSuccessHandler(file, response.result.path);
      }
    } catch (error) {
      setStatus(FILE_UPLOAD_STATUS.failed);
      showGenericErrorToast(error);
      if (fileUploadFailureHandler) fileUploadFailureHandler(file);
      throw error;
    }
  };

  const renderContent = (hasError: boolean = false) => (
    <div
      className={`flex justify-between items-center 
      ${
        showPreview && status === FILE_UPLOAD_STATUS.success
          ? ''
          : 'p-5 rounded-lg border  border-dashed'
      }w-full min-h-[93px] 
  
        ${
          hasError && status === FILE_UPLOAD_STATUS.failed
            ? 'border-red'
            : 'border-gray-200'
        }`}>
      {status === FILE_UPLOAD_STATUS.uploading ||
      (status === FILE_UPLOAD_STATUS.success && !showPreview) ? (
        <div className="flex flex-row grow items-center space-x-4 w-full leading-7">
          <FileUploaderIcon className="shrink-0 m-4" />
          <div className="flex flex-col mr-2">
            <div
              className="overflow-hidden shrink w-52 text-[16px] 
            font-normal text-black/80 text-ellipsis  whitespace-nowrap">
              {uploadedFile?.name}
            </div>
            <div className="flex flex-row space-x-2 text-[14px] text-black/60">
              <span>{`${bytesToMB(uploadedFile?.size)}MB`}</span>
              <span>
                {status === FILE_UPLOAD_STATUS.success
                  ? translate('fileUpload.complete')
                  : translate('fileUpload.loading')}
              </span>
            </div>
          </div>
          <div className="flex grow" />
          <DeleteIcon
            className="shrink-0 m-4 cursor-pointer delete-icon"
            onClick={handleRemoveFileClick}
          />
          {status === FILE_UPLOAD_STATUS.success ? (
            <SuccessIcon className="w-10 h-10" />
          ) : (
            <RefreshIcon className="shrink-0 animate-rotate" />
          )}
        </div>
      ) : (
        <>
          {FILE_UPLOAD_STATUS.success === status &&
          showPreview &&
          uploadedFile ? (
            <div className="w-full">
              <ImagePreviewCard
                name={name}
                isEdit={true}
                handleDeleteClick={handleRemoveFileClick}
                imageUrl={URL.createObjectURL(uploadedFile)}
                fileName={uploadedFile?.name}
                fileSize={Number(bytesToMB(uploadedFile?.size))}
                aspectRatio={aspectRatio}
              />
            </div>
          ) : (
            <div className="flex flex-row grow items-center space-x-4 w-full leading-7 text-red">
              <UploadFailedIcon className="m-4" />
              <div className="flex flex-col mr-2 bg-red  ">
                <span className="text-[16px]">
                  {translate('fileUpload.uploadFailed')}
                </span>
                <div className="flex flex-row space-x-2 text-[14px]">
                  <span>{translate('fileUpload.fileTooLarge')}</span>
                  <span>{translate('fileUpload.failed')}</span>
                </div>
              </div>
              <div className="flex grow" />
              <div className="ml-auto cursor-pointer">
                <DeleteIcon
                  className="delete-icon"
                  onClick={handleRemoveFileClick}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  useEffect(() => {
    if (resetUploadedFile) handleRemoveFileClick();
  }, [resetUploadedFile]);

  useEffect(() => {
    if (previousFile) {
      setUploadedFile(previousFile);
      setStatus(FILE_UPLOAD_STATUS.success);
    }
  }, [previousFile]);

  useEffect(() => {
    if (retryUpload) {
      let fileN = uploadedFile;
      handleRemoveFileClick();
      handleFileUpload(fileN as File);
    }
  }, [retryUpload]);

  return (
    <>
      {uploadedFile && enableCrop && (
        <div className="w-full h-full bg-lightGrey">
          <div className="flex justify-center items-center max-w-[408px]">
            <Suspense fallback={<></>}>
              <div>
                {status === FILE_UPLOAD_STATUS.uploading ? (
                  <div className="w-full h-full bg-lightGrey"></div>
                ) : (
                  <ImageCropper
                    src={URL.createObjectURL(uploadedFile)}
                    file={{
                      name: uploadedFile.name,
                      type: uploadedFile.type,
                      size: uploadedFile.size
                    }}
                    handleCropComplete={handleUpload}
                    aspectRatio={aspectRatio}
                    imageInfo={imageInfo}
                    handleClose={() => {}}
                    isInModal={false}
                  />
                )}
              </div>
            </Suspense>
          </div>
        </div>
      )}
      {status
        ? renderContent(
            !isEmpty(errors) &&
              !!(errors[name] as { message?: string })?.message
          )
        : !uploadedFile && (
            <FileDropZone
              supportedFormats={supportedFormats}
              onFileUpload={handleFileUpload}
              maxSizeInMb={sizeInMb}
              hasError={
                !isEmpty(errors) &&
                !!(errors[name] as { message?: string })?.message
              }
              isDisabled={isDisabled}
              customUploadText={customUploadText}
            />
          )}
      {!isEmpty(errors) && (errors[name] as { message?: string })?.message && (
        <p className="mx-[14px] mt-[3px] text-xs leading-5 text-red">
          {(errors[name] as { message?: string })?.message}
        </p>
      )}
    </>
  );
};

export default ImageUploader;
